import React from 'react'
import { CSSObject } from '@emotion/react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Heading1,
  JustifyContent,
  Overflow,
  Text,
  TextAlign,
  VisuallyHidden,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { useMarketingPricingConfig } from '@library/api-pricing'

import { CWCAppearance } from '../CWCAppearance/CWCAppearance'
import {
  CWCDiscountPills,
  HERO_DISCOUNT_CONTAINER_WIDTH,
} from '../CWCDiscountPills/CWCDiscountPills'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { CWCNewBadge } from '../CWCNewBadge/CWCNewBadge'
import { CWCHeroBackground } from './components/CWCHeroBackground/CWCHeroBackground'
import { DiscountBox } from '../AboveTheFoldSection/components/DiscountBanner/config'
import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../tracking'

export type CWCHeroProps = {
  discountBoxes: DiscountBox[]
  /** Sign-up destination the CTA links to (the anchor `href` — a real navigation target and no-JS fallback). */
  signupUrl: string
  /** Shared signup handler (applies the promo code, then redirects), matching the What's New and First Box CTAs. */
  onCTAClick: () => void
}

// Layout reasoning, per-breakpoint derivations and measured contrast: see design.md
// ("Implementation notes") and ui-design.md in
// openspec/changes/update-cwc-hero-image-and-whats-new-cta/.
//
// 4-value responsive arrays map to [base(<640), 640+, 768+ (tablet), 1024+ (desktop)] — a 3-value
// array lumps every width >=768 onto the desktop value.

// Sizes must go through responsiveStyle; a plain fontSize loses to Citrus's own @media default.
// The small-screen reduction is gated on WIDTH, never height: a viewport-height query re-evaluates
// when mobile Safari's URL bar collapses, resizing the headline mid-scroll.
const Headline = withCitrusTheme(Heading1, (theme) => [
  { fontWeight: 800, marginTop: '10px' } as CSSObject,
  theme.responsiveStyle(['40px', '40px', '56px', '72px'], ['fontSize']) as CSSObject,
  theme.responsiveStyle(['44px', '44px', '64px', '80px'], ['lineHeight']) as CSSObject,
  {
    '@media (max-width: 359px)': {
      fontSize: '32px',
      lineHeight: '36px',
    },
  } as CSSObject,
  // Paired with `HeroRoot`'s own `max-height: 900px` floor reduction below — change them together.
  {
    '@media (max-height: 900px) and (min-width: 1024px)': {
      fontSize: '56px',
      lineHeight: '64px',
    },
  } as CSSObject,
])

// Responsive line breaks for the headline: a display:block span forces the wrap. Must stay a <span>
// — a Box emits a <div>, invalid inside an <h1> and a hydration-split risk. The arrays switch at 768,
// not 640, so the 640–767 band keeps the mobile wrap alongside the mobile headline size.
const BreakMobileDesktop = withCitrusTheme('span', (theme) =>
  theme.responsiveStyle(['block', 'block', 'none', 'block'], ['display']),
)
const BreakTablet = withCitrusTheme('span', (theme) =>
  theme.responsiveStyle(['none', 'none', 'block', 'none'], ['display']),
)

// Content column. All padding and the gap live here rather than as Citrus `paddingV`/`gap` JSX props,
// so there is one source of truth per breakpoint. Mobile/tablet have no fixed gap — the gap is
// adaptive via `space-between` + `flex-grow` on the JSX below.
const ContentWrapper = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle([null, '0px', '0px', '44px'], ['gap']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '24px', '128px'], ['paddingLeft']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '24px', '24px'], ['paddingRight']) as CSSObject,
  // Desktop is intentionally `0px`, not the 80px platform section-padding standard, which does not fit
  // the shorter Hero — its vertical room comes from the `44px` gap and `justifyContent: Center`.
  theme.responsiveStyle(['32px', '40px', '64px', '0px'], ['paddingTop']) as CSSObject,
  theme.responsiveStyle(['16px', '40px', '40px', '0px'], ['paddingBottom']) as CSSObject,
])

const EyebrowText = withCitrusTheme(Text, (theme) => [
  { fontWeight: 700 } as CSSObject,
  theme.responsiveStyle(['17px', '17px', '24px', '28px'], ['fontSize']) as CSSObject,
  theme.responsiveStyle(['17.85px', '17.85px', '32px', '36px'], ['lineHeight']) as CSSObject,
])

// Carries the spacing and width the shared CWCLinkButton doesn't own itself.
const CTAWrapper = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(['8px', '18px', '18px', '18px'], ['marginTop']) as CSSObject,
  theme.responsiveStyle(['100%', '345px', '345px', '345px'], ['width']) as CSSObject,
  // Mobile cap shares `CWCDiscountPills`' constant so the CTA stays aligned with the discount banner
  // it sits under. `max-width`, not a fixed width, so it still shrinks on narrower phones.
  theme.responsiveStyle(
    [HERO_DISCOUNT_CONTAINER_WIDTH, null, null, null],
    ['maxWidth'],
  ) as CSSObject,
])

const BenefitsContainer = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(['6px', '12px', '12px'], ['marginTop']) as CSSObject),
}))

const PriceText = withCitrusTheme(Text, (theme) => ({
  color: theme.color(Color.White),
}))

// Heights are per-breakpoint and viewport-relative, each sized so the next section stays partly visible
// without scrolling, with a pixel floor so content can't drop below the fold on short viewports.
const HeroRoot = withCitrusTheme(Box, (theme) => [
  // Dark fallback matching the background image's top edge, in case a band's framing leaves a strip
  // uncovered.
  { backgroundColor: '#20130b' } as CSSObject,
  // Keep `svh`, not `dvh`: `dvh` live-tracks the mobile toolbar as it auto-hides, which would reflow
  // the CTA/discount group mid-scroll. Mobile subtracts a fixed `112px` (nav + reveal) rather than a
  // percentage so the reveal is the same height on every phone.
  {
    minHeight: 'max(calc(100vh - 112px), 570px)',
    '@supports (height: 100svh)': { minHeight: 'max(calc(100svh - 112px), 570px)' },
  } as CSSObject,
  theme.responsiveStyle([null, '812px', 'auto', 'auto'], ['height', 'minHeight']) as CSSObject,
  // Tablet
  {
    '@media (min-width: 768px) and (max-width: 1023px)': {
      minHeight: 'max(81.6vh, 600px)',
    },
  } as CSSObject,
  // Landscape phones — capped at 1023px so short tablet/desktop windows keep their own treatment.
  // Must reset `height` to `auto`: the explicit `height` above otherwise wins over this `min-height`.
  {
    '@media (max-height: 500px) and (max-width: 1023px)': {
      height: 'auto',
      minHeight: 'max(70vh, 570px)',
      '@supports (height: 100svh)': { minHeight: 'max(70svh, 570px)' },
    },
  } as CSSObject,
  // Desktop: both terms are Figma's 638px Hero over its 832px frame, so neither silently wins at the
  // reference size.
  {
    '@media (min-width: 1024px)': {
      minHeight: 'max(77vh, 638px)',
    },
  } as CSSObject,
  // Paired with `Headline`'s own `900px` exception above: the smaller headline lowers content's natural
  // minimum, so the floor drops to match.
  {
    '@media (min-width: 1024px) and (max-height: 900px)': {
      minHeight: 'max(77vh, 530px)',
    },
  } as CSSObject,
])

export function CWCHero({ discountBoxes, signupUrl, onCTAClick }: CWCHeroProps) {
  const defaultPricing = { pricePerPortion: '3.20', discountedPricePerPortion: '1.60' }
  const pricing = useMarketingPricingConfig()
  const pricePerPortion = pricing?.pricePerPortion ?? defaultPricing.pricePerPortion
  const discountedPricePerPortion =
    pricing?.discountedPricePerPortion ?? defaultPricing.discountedPricePerPortion

  const handleSignupClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // The CTA is a real <a href> (no-JS fallback), so suppress the raw navigation in favour of the
    // shared handler.
    event.preventDefault()
    onCTAClick()
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, LandingPageContentSections.HERO)
  }

  return (
    <HeroRoot
      data-testid="hero"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      // Flex column at mobile and desktop only, so `ContentWrapper` can fill it via `flex-grow`;
      // tablet fills a real fixed height with `height: 100%` instead.
      display={[Display.Flex, null, null, Display.Flex]}
      flexDirection={[FlexDirection.Column, null, null, FlexDirection.Column]}
    >
      <CWCHeroBackground />
      <ContentWrapper
        data-testid="heroContent"
        position={DisplayPosition.Relative}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        // Mobile/tablet pin the header group to the top and the offer group to the bottom, letting the
        // gap between them grow. Desktop centres instead, so its fixed `44px` gap stays consistent
        // across viewport heights and the spare room becomes margin around the block.
        justifyContent={[
          JustifyContent.SpaceBetween,
          JustifyContent.SpaceBetween,
          JustifyContent.SpaceBetween,
          JustifyContent.Center,
        ]}
        alignItems={[AlignItems.Center, AlignItems.Center, AlignItems.Center, AlignItems.FlexStart]}
        // `flex-grow`, not `height: 100%`, at mobile and desktop: `HeroRoot` only sets `min-height`
        // there, which a percentage height can't resolve against, so `justifyContent` would have no
        // room to work with. Tablet has a real fixed height, hence `height: 100%`.
        flexGrow={[1, null, null, 1]}
        // Desktop slot must be an explicit 'auto': a null slot doesn't reset a Citrus responsive-array
        // prop, it lets the previous breakpoint's value cascade forward.
        height={[null, '100%', '100%', 'auto']}
      >
        <Box
          width="100%"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={[
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.FlexStart,
          ]}
        >
          <Box display={Display.Flex} alignItems={AlignItems.Center} gap={0.75}>
            {/* The badge is decorative (aria-hidden), so "New" is kept as visually-hidden text to
                preserve "Meet the New Gousto" for assistive tech. */}
            <EyebrowText color={Color.White}>Meet the </EyebrowText>
            <VisuallyHidden>New </VisuallyHidden>
            {/* trigger="load" because the badge is above the fold; inline-flex avoids a line-box gap. */}
            <CWCAppearance trigger="load" display="inline-flex">
              <CWCNewBadge width={['62px', null, '67px']} height={['44px', null, '48px']} />
            </CWCAppearance>
            <EyebrowText color={Color.White}>Gousto</EyebrowText>
          </Box>
          <Headline
            color={Color.White}
            fontFamily={FontFamily.ExtraBold}
            textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
          >
            {/* mobile/desktop: "The recipe box / that works / around you";
                tablet: "The recipe box that / works around you" (design 183:141) */}
            {'The recipe box'}
            <BreakMobileDesktop />
            {' that '}
            <BreakTablet />
            {'works'}
            <BreakMobileDesktop />
            {/* Leading space is inside the string so prettier can't drop it — tablet renders this
                inline as "works around you". */}
            {' around you'}
          </Headline>
        </Box>
        <Box
          width="100%"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={[
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.FlexStart,
          ]}
        >
          <CWCDiscountPills
            discountBoxes={discountBoxes}
            price={{ pricePerPortion, discountedPricePerPortion }}
          />
          <CTAWrapper>
            {/* fontSizeOverride restores this CTA's Figma 18px label (CWCLinkButton defaults to 16px).
                data-testing is the hook the promo/RAF regression journeys click — don't remove it. */}
            <CWCLinkButton
              href={signupUrl}
              onClick={handleSignupClick}
              fontSizeOverride="18px"
              data-testing="homepageAboveTheFoldCTA"
            >
              Get started
            </CWCLinkButton>
          </CTAWrapper>
          <BenefitsContainer
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            width={['100%', '400px', '400px']}
          >
            <PriceText
              size={2}
              textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
            >
              <span style={{ fontWeight: FontWeight.Bold }}>Free next-day delivery</span> on your
              1st box<span style={{ fontSize: '9px', verticalAlign: 'super' }}>†</span>
            </PriceText>
            <PriceText
              size={2}
              textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
            >
              <span style={{ fontWeight: FontWeight.Bold }}>Skip or cancel</span> for free anytime
            </PriceText>
          </BenefitsContainer>
        </Box>
      </ContentWrapper>
    </HeroRoot>
  )
}
